'use client';

// Optimization Changes Flag
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { RECAPTCHA_SITE_KEY } from '@/config/env';

interface RecaptchaProps {
  onChange: (token: string | null) => void;
}

export default function Recaptcha({ onChange }: RecaptchaProps) {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [safariZoomFix, setSafariZoomFix] = useState<React.CSSProperties>({});
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const ua = navigator.userAgent;
    const isSafari = ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
    const isZoomActive = window.innerWidth >= 768 && window.innerWidth <= 2000;

    if (!isSafari || !isZoomActive) return;

    setSafariZoomFix({ zoom: 1.25 });

    const styleId = 'safari-recaptcha-modal-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        body > div[style*="z-index: 2000000000"] {
          zoom: 1.25 !important;
        }
        body > div[style*="z-index: 2000000000"] > div {
          zoom: 1 !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, [isVisible]);

  return (
    <div ref={containerRef} className="v2-relative v2-min-h-[78px]" style={safariZoomFix}>
      {isVisible && <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} size="normal" onChange={onChange} />}
    </div>
  );
}
